'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { CardWithBGType } from '@/types/ui';
import { ArrowLeftRight, DollarSign, CreditCard } from '@/public/images/icons/icons';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function WhatCanDo() {
    const t = useTranslations('WhatCanDo');

    const cards: CardWithBGType[] = [
        {
            id: 'send',
            title: 'option_1_title',
            description: 'option_1_desc',
            bg_class: 'bg-[#F1F5FD]',
            bg_icon: 'bg-primary',
            image: '/images/homepage/send_money.webp',
            icon: <ArrowLeftRight width={30} height={30} className="shrink-0" />,
        },
        {
            id: 'request',
            title: 'option_2_title',
            description: 'option_2_desc',
            bg_class: 'bg-[#E2EFEA]',
            bg_icon: 'bg-green-500',
            image: '/images/homepage/request_money.webp',
            icon: <DollarSign width={30} height={30} viewBox="0 0 18 20" className="shrink-0" />,
        },
        {
            id: 'split',
            title: 'option_3_title',
            description: 'option_3_desc',
            bg_class: 'bg-[#EFEBE5]',
            bg_icon: 'bg-orange-500',
            image: '/images/homepage/split_bill.webp',
            icon: <CreditCard width={30} height={30} className="shrink-0" />,
        },
    ];

    return (
        <motion.section
            className="w-full bg-white py-16 sm:py-24 px-6 sm:px-10 lg:px-16"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeInUp}
        >
            <div className="max-w-8xl mx-auto">
                <div className="mb-12 sm:mb-16">
                    <h4 className="text-xs font-medium tracking-wider text-gray-400 uppercase">
                        kuik.me
                    </h4>
                    <span className="text-xs sm:text-sm font-bold tracking-widest color-primary uppercase">
                        {t('subtitle')}
                    </span>
                    <h2 className="text-3xl sm:text-5xl font-extrabold text-black tracking-tight mt-3 leading-tight">
                        {t('title')}
                    </h2>
                    <p className="text-sm sm:text-base text-gray-500 font-medium mt-4 leading-relaxed">
                        {t('description')}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
                    {cards.map((card: CardWithBGType) => (
                        <div
                            key={card.id}
                            className={`${card.bg_class} rounded-3xl overflow-hidden flex flex-col sm:grid sm:grid-cols-2 lg:flex lg:flex-row justify-between transition-transform duration-300 hover:-translate-y-1`}
                        >
                            <div className="p-8 sm:p-10 flex flex-col items-start justify-start">
                                <div className={`${card.bg_icon} p-3 rounded-2xl flex items-center justify-center mb-6 shadow-sm`}>
                                    {card.icon}
                                </div>
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight leading-snug mb-3">
                                    {t(card.title)}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-normal">
                                    {t(card.description)}
                                </p>
                            </div>
                            <div className="relative w-full min-w-4/9 h-64 sm:h-full min-h-[520px]">
                                <Image
                                    src={card.image}
                                    alt={t(card.title)}
                                    fill
                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 50vw"
                                    className="object-cover object-center"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </motion.section>
    );
}